import type { Auth0Client, Auth0ClientOptions, LogoutOptions, RedirectLoginOptions } from "@auth0/auth0-spa-js";

interface AuthConfig {
  loginDomain: string;
  clientId: string;
  audience: string;
  redirectUri: string;
}

interface LoginState {
  target?: string;
}

export const buildAuth0Options = (config: AuthConfig): Auth0ClientOptions => ({
  domain: config.loginDomain,
  clientId: config.clientId,
  authorizationParams: {
    redirect_uri: config.redirectUri,
    audience: config.audience,
    scope: "openid profile email ltk.publisher",
    "ext-creator_login": "true"
  },
  cacheLocation: "localstorage"
});

export class CreatorAuth {
  private client: Auth0Client;
  private navigate: (target: string) => Promise<void>;

  constructor(client: Auth0Client, navigate: (target: string) => Promise<void>) {
    this.client = client;
    this.navigate = navigate;
  }

  getAccessToken() {
    return this.client.getTokenSilently();
  }

  getIdTokenClaims() {
    return this.client.getIdTokenClaims();
  }

  isAuthenticated() {
    return this.client.isAuthenticated();
  }

  login({ options, next }: { options?: RedirectLoginOptions<LoginState>; next?: string } = {}) {
    return this.client.loginWithRedirect({
      ...options,
      appState: { ...options?.appState, target: options?.appState?.target || next || "/logged-in" },
      authorizationParams: { ...options?.authorizationParams, "ext-creator_login": "true" }
    });
  }

  async handleRedirectCallback() {
    const { appState } = await this.client.handleRedirectCallback<LoginState>();
    const target = appState?.target;
    await this.navigate(target?.startsWith("/") && !target.startsWith("//") ? target : "/logged-in");
  }

  logout(options?: LogoutOptions) {
    return this.client.logout(options);
  }
}
